// <copyright file="DleRecordTypeQuery.tsx">
// © 2024 Florida Department of Transportation
//
// The copyright to the computer program(s) and source code herein is
// the property of the Florida Department of Transportation.
// The program(s) and source code may be used and/or copied only with the
// written permission of the Florida Department of Transportation or
// in accordance with the terms and conditions stipulated in the
// agreement/contract under which the program(s) and source code have
// been supplied.
// </copyright>

import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import React, { useState as _useState, useContext, useEffect } from "react";
import styles from "src/components/DataLakeExplorer/DleRecordTypeQuery.module.css";
import ConfigContext from "src/context/ConfigContext";
import { getAwsFederatedSigninUrl, LoadingStatus } from "src/utils/SharedUtils";
import type { FC } from "react";
import type { IConfig } from "src/utils/ConfigLoader";

// Properties to initialize this component
interface DleRecordTypeQueryProps {
    useState?: typeof _useState; // optional hook override (for tests)
    recordType: string;
}

// Data model for sample queries
interface SampleQuery {
    schemaName: string;
    schemaVersion: string | null;
    queryInfo: string | null;
    queryText: string | null;
}

// Sample queries
const sampleQueryList: SampleQuery[] = [
    {
        schemaName: "Ford",
        schemaVersion: "v6",
        queryInfo: `The sample query below pre-filters by indexed partitions (year, month, day),
        then filters by record details to find specific metrics, returning a sample of 20 records.`,
        queryText: `select * from ford_v6
where year = 2023 and month = 7 and day = 20
and (
    fordrecord.metrics.odometer is not null
    or fordrecord.metrics.fuellevel is not null
    or fordrecord.metrics.tirepressure is not null
    or fordrecord.metrics.oilliferemaining is not null
    or fordrecord.metrics.batteryvoltage is not null
)
limit 20;
`,
    },
    {
        schemaName: "GeoTabDeviceStatus",
        schemaVersion: "v1",
        queryInfo: `The sample query below pre-filters by indexed partitions (year, month, day),
        then filters by record details to find active bubble light events for the specified day.`,
        queryText: `select * from geotabdevicestatus_v1
where year = 2025 and month = 6 and day = 10
and any_match(geotabdevicestatusrecord.exceptionEvents, ee -> ee.ruleName = 'Bubble Lights')
limit 20;
`,
    },
    {
        schemaName: "Image",
        schemaVersion: "v2",
        queryInfo: `The sample query below filters by indexed partitions (year, month, day)
        to find image data for the specified day.`,
        queryText: `SELECT *
FROM image_v2
WHERE year = 2025 AND month = 11 AND day = 10
LIMIT 20;
`,
    },
    {
        schemaName: "Map",
        schemaVersion: "v11",
        queryInfo: `The sample query below pre-filters by indexed partitions (year, month, day, hour, minute),
        then filters by record details to find J2735 Maps, returning a sample of 20 records. Record detail filters for
        "metadata.source.producer.id" and "metadata.source.device.id" are used to retrieve data from a specific
        known district J2735 forwarder or host (producer) and device. This example also illustrates accessing
        a specific value from an array, such as the first absolute location. The retrieved intersection_id may be
        used to link data to corresponding SPaT records in the data lake.
        <br/><br/>
        NOTE: Queries prior to schema v11 must pre-filter by $file_modified_time, rather than indexed partitions.`,
        queryText: `select metadata.time.occurred,
    metadata.time.ingested,
    metadata.source.producer.id as producer_id,
    metadata.source.device.id as device_id,
    maprecord.mapdata.intersection.id as intersection_id,
    metadata.locations[1].points[1].absolute as ref_location
from map_v11
where year = 2025 and month = 2 and day = 15 and hour = 15 and minute = 5
    and metadata.source.producer.id = '3b1c55bdc20362423648d37d66d45877b26c48c1eaf54044aa5f9fe9ea69c729'
    and metadata.source.device.id = '024c23c152566306773c57802d7b2fb2ebf1006fe61de5d9fbca7dd4defb092b'
order by metadata.time.ingested desc
limit 20;
`,
    },
    {
        schemaName: "Message",
        schemaVersion: "v9",
        queryInfo: `The sample query below pre-filters by indexed partitions (year, month, day, hour),
        then filters by record details to find J2735 TIM messages sourced from SunGuide, returning a
        sample of 20 records.`,
        queryText: `select metadata.partitionkey,
    metadata.locations as presentation_regions,
    metadata.time.occurred as start_time,
    metadata.time.duration as duration,
    messagerecord.content as content
from message_v9
where year = 2024 and month = 7 and day = 20 and hour = 15
    and metadata.source.producer.name = 'SunGuide'
    and messagerecord.type = 'tim'
limit 20;
`,
    },
    {
        schemaName: "RoadwayEvent",
        schemaVersion: "v9",
        queryInfo: `The sample query below pre-filters by indexed partitions (year, month, day, hour),
        then filters by record details to find events sourced from SunGuide with lane closures,
        returning a sample of 20 records.`,
        queryText: `select metadata.source.producer.name as producer,
    metadata.partitionkey,
    metadata.time.occurred,
    lc.laneclosures as lane_closures,
    metadata.locations[1].points[1].relative as location_details,
    roadwayeventrecord as event_details
from roadwayevent_v9, unnest(roadwayeventrecord.laneclosures) lc(laneclosures)
where year = 2024 and month = 7 and day = 20 and hour = 15
    and metadata.source.producer.name = 'SunGuide'
    and cardinality(roadwayeventrecord.laneclosures) > 0
    and laneclosures.type != 'clear'
limit 20;
`,
    },
    {
        schemaName: "Spat",
        schemaVersion: "v8",
        queryInfo: `The sample query below pre-filters by indexed partitions (year, month, day, hour, minute),
        returning a sample of 20 records. Because this is a HIGH VOLUME data source, all indexed partitions are
        use to pre-filter for the smallest possible time range. The retrieved intersection_id may be used to link
        data to a corresponding Map records in the data lake.`,
        queryText: `select spatrecord.intersection.id as intersection_id, *
from spat_v8
where year = 2024 and month = 7 and day = 20 and hour = 15 and minute = 5
limit 20;
`,
    },
    {
        schemaName: "Traffic",
        schemaVersion: "v6",
        queryInfo: `The sample query below pre-filters by indexed partitions (year, month, day, hour, minute),
        returning a sample of 20 records. Because this is a HIGH VOLUME data source, all indexed partitions are
        use to pre-filter for the smallest possible time range. Additionally, a record detail filter is used to show
        only those sourced from SunGuide where the relative roadway name is known.`,
        queryText: `select metadata.locations[1].points[1].relative.roadway, *
from traffic_v6
where year = 2024 and month = 7 and day = 20 and hour = 15 and minute = 5
    and metadata.source.producer.name = 'SunGuide'
    and cardinality(metadata.locations[1].points) > 0
    and metadata.locations[1].points[1].relative.roadway is not null
limit 20;
`,
    },
    {
        schemaName: "TrafficSignal",
        schemaVersion: "v4",
        queryInfo: `The sample query below filters by indexed partitions (year, month, day)
        to find traffic signal data for the specified day.`,
        queryText: `SELECT *
FROM trafficsignal_v4
WHERE year = 2025 AND month = 11 AND day = 10
LIMIT 20;
`,
    },
        {
        schemaName: "TrafficSignalSchedule",
        schemaVersion: "v3",
        queryInfo: `The sample query below filters by indexed partitions (year, month, day)
        to find traffic signal schedule data for the specified day.`,
        queryText: `SELECT *
FROM trafficsignalschedule_v3
WHERE year = 2025 AND month = 11 AND day = 10
LIMIT 20;
`,
    },
    {
        schemaName: "TrafficSignalTimingPlan",
        schemaVersion: "v4",
        queryInfo: `The sample query below filters by indexed partitions (year, month, day)
        to find traffic signal timing plan data for the specified day.`,
        queryText: `SELECT *
FROM trafficsignaltimingplan_v4
WHERE year = 2025 AND month = 11 AND day = 10
LIMIT 20;
`,
    },
    {
        schemaName: "TravelTime",
        schemaVersion: "v12",
        queryInfo: `The sample query below filters by indexed partitions (year, month, day)
        to find travel time data for the specified day.`,
        queryText: `SELECT *
FROM traveltime_v12
WHERE year = 2025 AND month = 11 AND day = 10
LIMIT 20;
`,
    },
    {
        schemaName: "Vehicle",
        schemaVersion: "v8",
        queryInfo: `The sample query below pre-filters by indexed partitions (year, month, day, hour, minute),
        returning a sample of 20 records. Because this is a HIGH VOLUME data source, all indexed partitions are
        use to pre-filter for the smallest possible time range. Additionally, a record detail filter is used to show
        only those where a vehicle speed threshold is exceeded. Before querying data lake Vehicle records,
        users are advised to view Data Ingestion Dashboard, Health Status Dashboard, and/or RSU Coverage Dashboard
        to find a time period where vehicle data exists for a district, agency, or geospatial area of interest.`,
        queryText: `select metadata.time.occurred,
    vehiclerecord.basicsafetymessage.coredata.location,
    vehiclerecord.basicsafetymessage.coredata.speed,
    vehiclerecord.basicsafetymessage.coredata.heading,
    vehiclerecord.basicsafetymessage.coredata.angle,
    vehiclerecord.basicsafetymessage.coredata.vehiclesize
from vehicle_v8
where year = 2024 and month = 7 and day = 20 and hour = 15 and minute = 5
    and vehiclerecord.basicsafetymessage.coredata.speed > 100
limit 20;
`,
    },
    {
        schemaName: "Weather",
        schemaVersion: "v7",
        queryInfo: `The sample query below pre-filters by indexed partitions (year, month, day, hour, minute),
        returning a sample of 20 records. Additionally, a record detail filter is used to show only those
        sourced from SunGuide RWIS subsystem.`,
        queryText: `select *
from weather_v7
where year = 2024 and month = 7 and day = 20 and hour = 15 and minute = 5
    and metadata.source.producer.name = 'SunGuide'
    and metadata.partitionkey like '%rwis%'
limit 20;
`,
    },
];

// Info/error message popup
const toast = React.createRef<Toast>();

// Main content focus item
const mainContentRef = React.createRef<HTMLAnchorElement>();

// Best practices elment
const bestPractices = () => (
    <div>
        <label htmlFor="best-practices">Best Practices</label>
        <div id="best-practices" title="best-practices">
            <ul>
                <li>
                    Limit data scans to reduce query runtime and cost
                    <ul>
                        <li>
                            Filter by partition indexes (year, month, day, hour,
                            minute), then by then other fields
                        </li>
                        <li>
                            {
                                'Run queries on small date ranges and use "limit" clause to verify the query before scanning larger data'
                            }
                        </li>
                        <li>
                            {
                                'To scan data across schema versions, use a "union all" query.'
                            }
                        </li>
                    </ul>
                </li>
                <li>
                    References
                    <ul>
                        <li>
                            <a
                                ref={mainContentRef}
                                target="_blank"
                                rel="noreferrer"
                                href="https://docs.aws.amazon.com/athena/latest/ug/select.html"
                            >
                                Athena select query
                            </a>
                        </li>
                        <li>
                            <a
                                target="_blank"
                                rel="noreferrer"
                                href="https://docs.aws.amazon.com/athena/latest/ug/select.html"
                            >
                                Athena array item elements
                            </a>
                        </li>
                        <li>
                            <a href="https://docs.aws.amazon.com/athena/latest/ug/arrays-and-aggregation.html">
                                Athena array aggregation functions
                            </a>
                        </li>
                        <li>
                            <a href="https://docs.aws.amazon.com/athena/latest/ug/geospatial-functions-v3.html">
                                Athena geospatial functions
                            </a>
                        </li>
                    </ul>
                </li>
            </ul>
        </div>
    </div>
);

/**
 * Determines whether to display a progress spinner to indicate data loading
 * @param loadingStatus Status of loading sign-in URL for AWS console
 * @returns Indicator whether the progress spinner should be displayed
 */
function shouldDisplaySpinner(loadingStatus: LoadingStatus) {
    return loadingStatus === LoadingStatus.loading;
}

/**
 * Determines whether to display error message
 * @param errorMessage Value of error message
 * @param loadingStatus Status of loading sign-in URL for AWS console
 * @returns Indicator whether error message should be displayed
 */
function shouldDisplayError(
    errorMessage: string,
    loadingStatus: LoadingStatus
) {
    return errorMessage !== "" && loadingStatus === LoadingStatus.error;
}

/**
 * Determines whether to display component content (data is loaded)
 * @param errorMessage Error message
 * @returns Indicator whether the component content should be displayed
 */
function shouldDisplayContent(errorMessage: string) {
    return errorMessage === "";
}

/**
 * DLE record type query component
 * @param props Component props
 * @returns The component
 */
const DleRecordTypeQuery: FC<DleRecordTypeQueryProps> = (
    props: DleRecordTypeQueryProps
) => {
    console.debug(props);
    const configData = useContext(ConfigContext) as IConfig;
    const useState = props.useState ?? _useState;
    const [loadingStatus, setLoadingStatus] = useState(LoadingStatus.preload);
    const [errorMessage, setErrorMessage] = useState<string>("");

    /**
     * Hook on component mount/unmount
     */
    useEffect(() => {
        mainContentRef.current?.focus(); // Set focus element for accessibility
    }, []);

    // Sample query element
    const sampleQuery = (recordType: string) => (
        <div className={styles.SampleQuery}>
            <label htmlFor="sample-query">Sample Query</label>
            <div>
                The sample query below was created using {recordType} schema{" "}
                {getQuerySchemaVersion(recordType)}. Modifications may be
                required to use them on newer or older schema versions.
            </div>
            <div>{getQueryComponent(recordType)}</div>
        </div>
    );

    // Query details
    const getQueryComponent = (recordType: string) => {
        const query: SampleQuery | undefined = sampleQueryList.find(
            (i) => i.schemaName == recordType
        );
        if (query) {
            return (
                <>
                    <div dangerouslySetInnerHTML= {{ __html: query.queryInfo || ""}}/>
                    <div>{getQueryButtons(query.queryText)}</div>
                    <div>{getQueryText(query.queryText)}</div>
                </>
            );
        }
    };

    // Query schema version details
    const getQuerySchemaVersion = (recordType: string) => {
        const query: SampleQuery | undefined = sampleQueryList.find(
            (i) => i.schemaName == recordType
        );
        return query?.schemaVersion || null;
    };

    // Button panel
    const getQueryButtons = (queryText: string | null) => {
        if (queryText) {
            return (
                <div className={styles.ButtonPanel}>
                    {shouldDisplaySpinner(loadingStatus) && (
                        <div className="AwsLinkProgress">
                            <ProgressSpinner
                                style={{
                                    height: "30px",
                                    width: "30px",
                                    transform: "scale(1)",
                                }}
                                aria-label="Loading"
                            />
                        </div>
                    )}
                    <Button
                        title="Copy to clipboard"
                        label="Copy"
                        aria-controls="copy-query"
                        data-testid="copy-query-button"
                        onClick={() => navigator.clipboard.writeText(queryText)}
                    />
                    <Button
                        title="Open Athena query editor"
                        label="Try it!"
                        aria-controls="try-it"
                        data-testid="try-it-button"
                        onClick={() => doButtonAction(configData)}
                    />
                </div>
            );
        }
    };

    // Button action creates a new AWS federated sign-in token session and opens link in new window/tab
    const doButtonAction = (configData: IConfig) => {
        const fetchLink = async () => {
            const destination =
                "https://us-east-1.console.aws.amazon.com/athena/home/query-editor";
            console.debug(`encoded destination url: ${encodeURI(destination)}`);
            await getAwsFederatedSigninUrl(
                `${configData.apiBaseUrl}`,
                destination,
                toast.current,
                setLoadingStatus
            )
                .then((url) => {
                    // Browser popup blocker settings of user may need to be adjusted to allow new window/tab
                    console.debug(`Setting url: ${url}`);
                    console.debug(`Loading status: ${loadingStatus}`);
                    setErrorMessage("");
                    window.open(url, "_blank");
                })
                .catch((error) => {
                    // Loading status and toast message for errors done in util function
                    console.debug(`Setting url: not-found`);
                    console.debug(`Loading status: ${loadingStatus}`);
                    setErrorMessage(`${error.summary}: ${error.detail}`);
                });
        };
        fetchLink();
    };

    // Query text box
    const getQueryText = (queryText: string | null) => {
        if (queryText != null) {
            // Don't autoresize on InputTextarea, set initial rows and let user resize as needed
            return (
                <InputTextarea
                    id="query-text"
                    className={styles.QueryText}
                    value={queryText}
                    contentEditable={false}
                    spellCheck={false}
                    rows={20}
                />
            );
        }
    };

    // Build the main component
    return (
        <div
            className={styles.DleRecordTypeQuery}
            data-testid="DleRecordTypeQuery"
        >
            <Toast ref={toast} />
            {bestPractices()}
            {shouldDisplayError(errorMessage, loadingStatus) && (
                <div className="App-content-error">{errorMessage}</div>
            )}
            {shouldDisplayContent(errorMessage) &&
                sampleQuery(props.recordType)}
        </div>
    );
};

export default DleRecordTypeQuery;
